import {
  useGetAdverseEventTypes,
  useGetInterventionLevels,
  useGetMaintenanceTypes,
  useGetPoolRegulations,
  useGetReportTypes,
} from "../../../../shared/services/catalogService";
import type {
  NewFormData,
  PoolRegulationNewFormData,
  AdverseEventNewFormData,
} from "../../components/RegisterNewForm/RegisterNewForm.types";
import type { MaintenanceNewFormData } from "../../components/MaintenanceNewFormFields/MaintenanceNewFormFields.types";
import type { UpdateNewRequest } from "../../../../api/client/types/new.types";
import toast from "react-hot-toast";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ROUTES } from "../../../../routes/routes";
import { useUpdateNew } from "../../services/newService";
import { useQueryClient } from "@tanstack/react-query";
import {
  isMaintenanceReportType,
  isFirstAidStationReportType,
  isPoolRegulationReportType,
  isAdverseEventReportType,
  isLlamadoDeAtencionInterventionLevel,
} from "../../utils/reportTypeConstants";
import { useGetNewById } from "../../services/newService";
import { mapNewToInitialData } from "./mapNewToInitialData";
import { newQueryKeys } from "../../services/newService";

export const useEditNewPage = () => {
  const { newId } = useParams<{ newId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const { data: newItem, isLoading: isLoadingNew, isError: isErrorNew } = useGetNewById(newId ?? "");
  const { data: reportTypes } = useGetReportTypes();
  const { data: maintenanceTypes } = useGetMaintenanceTypes();
  const { data: poolRegulationTypes } = useGetPoolRegulations();
  const { data: interventionLevels } = useGetInterventionLevels();
  const { data: adverseEventTypes } = useGetAdverseEventTypes();

  const { mutate: updateNew, isPending: isUpdating } = useUpdateNew();

  const handleNavigateToDashboard = () => {
    const returnSearch =
      (location.state as { returnSearch?: string } | undefined)?.returnSearch ?? "?tab=novedades";
    navigate(ROUTES.DASHBOARD.ADMINISTRATOR + returnSearch);
  };

  const buildUpdatePayload = (
    data:
      | NewFormData
      | MaintenanceNewFormData
      | PoolRegulationNewFormData
      | AdverseEventNewFormData
  ): UpdateNewRequest => {
    const reportTypeId = parseInt(data.report_type_id, 10);
    const payload: UpdateNewRequest = {
      event_timestamp: data.event_timestamp,
      report_type_id: reportTypeId,
      event_description: data.event_description,
    };

    // security_officer_name: required for Mantenimiento, Eventos Adversos; optional for Pool Regulation (except Llamado de Atención 1/2); never for First Aid
    if (!isFirstAidStationReportType(reportTypeId, reportTypes) && data.security_officer_name) {
      const skipSecurityOfficer =
        isPoolRegulationReportType(reportTypeId, reportTypes) &&
        isLlamadoDeAtencionInterventionLevel(
          (data as PoolRegulationNewFormData).intervention_level_id,
          interventionLevels
        );
      if (!skipSecurityOfficer) {
        payload.security_officer_name = data.security_officer_name;
      }
    }

    if (isMaintenanceReportType(reportTypeId, reportTypes)) {
      const maintenanceData = data as MaintenanceNewFormData;
      if (maintenanceData.maintenance_type_id) {
        payload.maintenance_type_id = parseInt(maintenanceData.maintenance_type_id, 10);
      }
    }

    if (isPoolRegulationReportType(reportTypeId, reportTypes)) {
      const poolData = data as PoolRegulationNewFormData;
      if (poolData.pool_regulation_id) {
        payload.pool_regulation_id = parseInt(poolData.pool_regulation_id, 10);
      }
      if (poolData.intervention_level_id) {
        payload.intervention_level_id = parseInt(poolData.intervention_level_id, 10);
      }
    }

    if (isAdverseEventReportType(reportTypeId, reportTypes)) {
      const adverseData = data as AdverseEventNewFormData;
      if (adverseData.adverse_events_type_id) {
        payload.adverse_events_type_id = parseInt(adverseData.adverse_events_type_id, 10);
      }
      payload.spent_items = adverseData.spent_items ?? undefined;
      // Absent for Agresión Verbal/Física and Condiciones Meteorológicas
      if (adverseData.event_specific_factor_id) {
        payload.event_specific_factor_id = parseInt(adverseData.event_specific_factor_id, 10);
      }
      if (adverseData.adverse_event_intervention_level_id) {
        payload.adverse_event_intervention_level_id = parseInt(
          adverseData.adverse_event_intervention_level_id,
          10
        );
      }
    }

    return payload;
  };

  const handleFormSubmit = (
    data:
      | NewFormData
      | MaintenanceNewFormData
      | PoolRegulationNewFormData
      | AdverseEventNewFormData
  ) => {
    if (!newId) return;
    const updatePayload = buildUpdatePayload(data);
    updateNew(
      { news_id: newId, data: updatePayload },
      {
        onSuccess: (updatedNew) => {
          // Keep list and report preview invalidated so dashboard data is refetched
          queryClient.invalidateQueries({ queryKey: newQueryKeys.list() });
          queryClient.invalidateQueries({
            queryKey: [...newQueryKeys.root, "reportPreview"],
          });

          // Optimistically update the detail cache with the backend response
          // so the next time this news is opened, the edit page sees fresh data
          // immediately from the cache (and can refetch if invalidated later).
          queryClient.setQueryData(newQueryKeys.detail(newId), updatedNew);

          toast.success("Novedad actualizada correctamente.");
          handleNavigateToDashboard();
        },
      }
    );
  };

  const initialData = newItem ? mapNewToInitialData(newItem) : undefined;

  return {
    newId,
    newItem,
    isLoadingNew,
    isErrorNew,
    reportTypes: reportTypes ?? [],
    maintenanceTypes: maintenanceTypes ?? [],
    poolRegulationTypes: poolRegulationTypes ?? [],
    interventionLevels: interventionLevels ?? [],
    adverseEventTypes: adverseEventTypes ?? [],
    initialData,
    handleFormSubmit,
    handleNavigateToDashboard,
    isSubmitting: isUpdating,
    swimmer: newItem?.swimmer ?? undefined,
  };
};
